import React, { useEffect, useMemo, useState } from "react";
import {
  WebViewContainer,
  ContentContainer,
  CenteredColumn,
  FieldCard,
} from "./components/StyledComponents";
import { ReportForm } from "./components/ReportForm";

import { webviewData } from "./sample_data";

export default function App() {
  // These could be injected later (e.g., from the webview host or worker)
  const [deviceId] = useState("234355");

  const dimensions = window?.NEWSTORE?.dimensions || webviewData.dimensions;
  const theme = window?.NEWSTORE?.theme || webviewData.theme;

  const associateName = window?.NEWSTORE?.contextProps?.formData?.userInfo
    ? `${window.NEWSTORE.contextProps.formData.userInfo.firstName || ""} ${
        window.NEWSTORE.contextProps.formData.userInfo.lastName || ""
      }`.trim()
    : webviewData?.contextProps?.formData?.userInfo
    ? `${webviewData.contextProps.formData.userInfo.firstName || ""} ${
        webviewData.contextProps.formData.userInfo.lastName || ""
      }`.trim()
    : "Unknown Associate";

  const storeLocale = window?.NEWSTORE?.contextProps?.formData?.storeInfo
    ? window.NEWSTORE.contextProps.formData.storeInfo.locale || ""
    : webviewData?.contextProps?.formData?.storeInfo
    ? webviewData.contextProps.formData.storeInfo.locale || ""
    : "";
  const currentStoreId = window?.NEWSTORE?.contextProps?.formData?.storeInfo
    ? window.NEWSTORE.contextProps.formData.storeInfo.storeId || ""
    : webviewData?.contextProps?.formData?.storeInfo
    ? webviewData.contextProps.formData.storeInfo.storeId || ""
    : "";
  const currentStoreName = window?.NEWSTORE?.contextProps?.formData?.storeInfo
    ? window.NEWSTORE.contextProps.formData.storeInfo.label || ""
    : webviewData?.contextProps?.formData?.storeInfo
    ? webviewData.contextProps.formData.storeInfo.label || ""
    : "";

  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [operator] = useState(associateName);
  const [periodKey, setPeriodKey] = useState("Daily"); // 'Daily' | 'Weekly' | 'Monthly'
  const [businessDate, setBusinessDate] = useState("");
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', message }

  // helper formatters (not hooks)
  const formatYMD = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatYM = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}`;
  };

  // Load stores from Worker KV
  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        const res = await fetch("/api/stores", { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json(); // expects: [{ store_id, store_name }, ...]
        if (ac.signal.aborted) return;

        setStores(data);
        setStoreId((prev) => (prev ? prev : data[0]?.store_id || ""));
      } catch (err) {
        if (ac.signal.aborted) return;
        const fallback = [
          { store_id: "31434", store_name: "Store 31434" },
          { store_id: "31435", store_name: "Store 31435" },
        ];
        setStores(fallback);
        setStoreId((prev) => (prev ? prev : fallback[0]?.store_id || ""));
      }
    })();

    return () => ac.abort();
  }, [setStores, setStoreId]);

  // placeholders for label only
  const businessDatePlaceholder = useMemo(() => {
    switch (periodKey) {
      case "Daily":
        return "YYYY-MM-DD";
      case "Weekly":
        return "YYYY-MM-DD";
      case "Monthly":
        return "YYYY-MM";
      default:
        return "";
    }
  }, [periodKey]);

  // precompute limits (based on "today")
  const dateLimits = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // yesterday
    const y = new Date(today);
    y.setDate(today.getDate() - 1);

    // last Sunday (end of last full week)
    const d = new Date(today);
    const day = d.getDay();
    const mondayThisWeek = new Date(d);
    const deltaToMonday = (day + 6) % 7;
    mondayThisWeek.setDate(d.getDate() - deltaToMonday);
    const lastSunday = new Date(mondayThisWeek);
    lastSunday.setDate(mondayThisWeek.getDate() - 1);

    // previous month (for Monthly)
    const prevMonth = new Date(today);
    prevMonth.setDate(1);
    prevMonth.setMonth(today.getMonth() - 1);

    // five years ago from today
    const fiveYearsAgo = new Date(today);
    fiveYearsAgo.setFullYear(today.getFullYear() - 5);

    return { yesterday: y, lastSunday, prevMonth, fiveYearsAgo };
  }, []);

  useEffect(() => {
    // Clear the date when the period changes
    setBusinessDate("");
  }, [periodKey]);

  // Ensure default date based on period
  useEffect(() => {
    if (periodKey === "Daily") {
      const y = dateLimits.yesterday;
      const yesterdayStr = formatYMD(y);
      if (!businessDate) setBusinessDate(yesterdayStr);
    } else if (periodKey === "Weekly") {
      const ls = dateLimits.lastSunday;
      const lastSundayStr = formatYMD(ls);
      if (!businessDate) setBusinessDate(lastSundayStr);
    } else if (periodKey === "Monthly") {
      const pm = dateLimits.prevMonth;
      const prevMonthStr = formatYM(pm);
      if (!businessDate) setBusinessDate(prevMonthStr);
    }
  }, [periodKey, businessDate, dateLimits]);

  // for DatePicker selected prop – convert our string to Date
  const businessDateAsDate = useMemo(() => {
    if (!businessDate) return null;
    if (periodKey === "Monthly") {
      // businessDate is YYYY-MM
      const [yyyy, mm] = businessDate.split("-");
      return new Date(Number(yyyy), Number(mm) - 1, 1);
    }
    // else YYYY-MM-DD
    return new Date(businessDate + "T00:00:00");
  }, [businessDate, periodKey]);

  // this is called by the DatePicker in the form
  const handleBusinessDateChange = (date) => {
    if (!date) return;

    // clamp depending on period
    if (periodKey === "Daily") {
      let d = date;
      if (d > dateLimits.yesterday) {
        d = dateLimits.yesterday;
      }
      setBusinessDate(formatYMD(d));
      return;
    }

    if (periodKey === "Weekly") {
      let d = date;
      if (d > dateLimits.lastSunday) {
        d = dateLimits.lastSunday;
      }
      setBusinessDate(formatYMD(d));
      return;
    }

    if (periodKey === "Monthly") {
      let d = new Date(date);
      // for month view, we only care about year/month, clamp to prev month
      if (d > dateLimits.prevMonth) {
        d = dateLimits.prevMonth;
      }
      setBusinessDate(formatYM(d));
      return;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setSubmitting(true);
    const storeName =
      (stores || []).find((s) => s.store_id === storeId)?.store_name || "";

    const payload = {
      store_id: storeId,
      store_name: storeName,
      period: businessDate,
      period_key: periodKey,
      force,
      device_id: deviceId,
      operator,
    };

    try {
      const res = await fetch("/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const data = await res.json().catch(() => ({}));
      setStatus({
        type: "success",
        message: data.message || data.status || "Success",
      });
    } catch (err) {
      setStatus({ type: "error", message: err?.message ?? "Unknown error" });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------
  // Render
  // ---------------------
  const showMinimal = storeLocale && storeLocale !== "th-th";

  return (
    <WebViewContainer>
      <ContentContainer dimensions={dimensions} theme={theme}>
        <CenteredColumn>
          {showMinimal ? (
            <FieldCard>
              Reconciliation Summary Report is not supported for current store -
              ({currentStoreId}) {currentStoreName}.
            </FieldCard> // Previously the early return; now just render the minimal view
          ) : (
            <div>
              <FieldCard>Operator: {associateName}</FieldCard>
              <ReportForm
                stores={stores}
                storeId={storeId}
                setStoreId={setStoreId}
                periodKey={periodKey}
                setPeriodKey={setPeriodKey}
                businessDate={businessDate}
                businessDateAsDate={businessDateAsDate}
                onBusinessDateChange={handleBusinessDateChange}
                businessDatePlaceholder={businessDatePlaceholder}
                force={force}
                setForce={setForce}
                onSubmit={submit}
                submitting={submitting}
                status={status}
                // pass limits so the datepicker can disable future dates
                dateLimits={dateLimits}
              />
            </div>
          )}
        </CenteredColumn>
      </ContentContainer>
    </WebViewContainer>
  );
}
